import React, {useState, useEffect} from "react";
import {useHistory, Link} from "react-router-dom";
import {useObserver, observer} from "mobx-react-lite";
import {Menu, Button} from "antd";
import {LogoutOutlined, SettingOutlined, UserOutlined, MenuOutlined} from "@ant-design/icons";
import moment from "moment";
import {useTranslation} from "react-i18next";

import {useStore} from "../../hooks/useStore";
import {
    routes,
    customerRoutes,
    nonApiIntegratedCustomerRoutes,
    poolCustomerRoutes,
    poolAndUatCustomerRoutes
} from "../../routes/routes";
import {Loading} from "../_defaultComponents/Loading/Loading";

import {
    LayoutContainer,
    LayoutSider,
    RightLayout,
    LayoutHeader,
    LayoutFooter,
    ChildrenContainer,
    LayoutContent,
    LogoImg,
    CompanyImg,
    MenuText,
    BottomMenu,
    LogoContainer,
    MobileMenuToggle
} from "./layoutStyles";
import Logo from "../../public/images/logo.png";
import CompanyPlaceholder from "../../public/images/company-placeholder.jpg";
import config from "../../_core/config";
import VigoMobileLogo from "./VigoMobileLogo";
import {getStartRoute} from "../../utils/getStartRoute";
import {CustomerTheme} from "./customerTheme/CustomerTheme";
import {LoginLayout} from "../layouts/loginLayout/LoginLayout";
import {setSentryUser} from "../../index";
const {SubMenu} = Menu;
const mapPaths = ["/map", "/regions", "/heatmap", "/create-delivery-request"];
const isMap = pathname => mapPaths.includes(pathname);

export const Theme = observer(props => {
    const stores = useStore();
    const history = useHistory();
    const {i18n} = useTranslation();
    const [pathname, setPathname] = useState(
        getStartRoute({
            role: stores?.meStore?.role,
            isPoolCustomer: stores?.meStore?.isPoolCustomer,
            isUatCustomer: stores?.meStore?.isUatCustomer
        })
    );
    const [collapsed, setCollapsed] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const {isLoggedIn, role, company, companies} = stores.meStore;
    const image =
        company && company.photo
            ? `${config.getBasePublicUrl()}api/panel/v1/image/${company.photo}`
            : CompanyPlaceholder;
    const onCollapse = collapsed => {
        setCollapsed(collapsed);
    };

    useEffect(() => {
        history.listen(location => {
            stores.appStore.setCurrentPath(location.pathname);
        });
    });

    useEffect(() => {
        stores.appStore.setCurrentPath(history.location.pathname);
        if (isLoggedIn) {
            stores.appStore.getCompanyUserList();
            stores.appStore.getCompanyListFilter();
            setSentryUser(stores.meStore);
        } else {
            setSentryUser(null);
        }
    }, []);

    useEffect(() => {
        setPathname(history.location.pathname);
    }, [history.location.pathname]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const toLogout = async () => {
        await stores.appStore.setLoading(true);
        await stores.meStore.logout();
        await history.push("/login");
        await stores.appStore.setLoading(false);
    };

    const toAppConfig = async () => {
        await stores.appStore.setLoading(true);
        await history.push("/appconfig");
        await stores.appStore.setLoading(false);
    };

    const extra = [
        <React.Fragment key={1}>
            {stores.meStore.checkRole("appConfig") && (
                <Button
                    key="appConfig"
                    data-testid="appConfigButton"
                    onClick={() => toAppConfig()}
                    icon={<SettingOutlined />}
                />
            )}
            <Button
                onClick={() => i18n.changeLanguage(i18n.resolvedLanguage === "en" ? "tr" : "en")}
            >
                {i18n.resolvedLanguage === "en" ? "EN" : "TR"}
            </Button>

            <Button
                key="logout"
                data-testid="logoutButton"
                onClick={() => toLogout()}
                icon={<LogoutOutlined />}
            />
        </React.Fragment>
    ];

    const toProfile = async () => {
        await stores.appStore.setLoading(true);
        await history.push("/profile");
        await stores.appStore.setLoading(false);
    };

    extra.splice(
        0,
        0,
        company ? (
            <CompanyImg onClick={() => toProfile()} src={image} />
        ) : (
            <Button
                data-testid="profileButton"
                key="profile"
                onClick={() => toProfile()}
                icon={<UserOutlined />}
            />
        )
    );

    const isMenuActive = routePath => {
        if (window.location.pathname.indexOf(routePath) == -1) return false;

        if (window.location.pathname.replace("/panel", "").replace(routePath, "").length > 0)
            return false;

        return true;
    };

    const isPoolCustomer = stores.meStore.checkRole("isPoolCustomer");
    const isUATCustomer = stores.meStore.checkRole("isUatCustomer");
    const isAdmin = stores.meStore.checkRole("isAdmin");
    const isCompanyUser = stores.meStore.checkRole("isCompanyUser");

    const draggableSupsis = () => {
        const dragElement = elmnt => {
            let isDragging = false;
            let startX = 0,
                startY = 0;
            let hasMoved = false;
            let initialX = 0,
                initialY = 0;

            const dragMouseDown = e => {
                if (!e.target.closest("#ls-openButton")) return;

                e.preventDefault();
                e.stopPropagation();

                initialX = e.clientX;
                initialY = e.clientY;
                startX = e.clientX - elmnt.offsetLeft;
                startY = e.clientY - elmnt.offsetTop;
                isDragging = true;
                hasMoved = false;
                document.onmouseup = closeDragElement;
                document.onmousemove = elementDrag;
            };

            const elementDrag = e => {
                if (!isDragging) return;
                e.preventDefault();
                e.stopPropagation();

                if (Math.abs(e.clientX - initialX) > 5 || Math.abs(e.clientY - initialY) > 5) {
                    hasMoved = true;
                    elmnt.style.pointerEvents = "none";
                }

                const newLeft = e.clientX - startX;
                const newTop = e.clientY - startY;

                const maxX = window.innerWidth - 80;
                const maxY = window.innerHeight - 80;
                const minX = 20;
                const minY = 20;

                elmnt.style.left = Math.min(Math.max(minX, newLeft), maxX) + "px";
                elmnt.style.top = Math.min(Math.max(minY, newTop), maxY) + "px";
            };

            const closeDragElement = e => {
                if (hasMoved) {
                    e.preventDefault();
                    e.stopPropagation();
                    setTimeout(() => {
                        elmnt.style.pointerEvents = "auto";
                    }, 100);
                }

                isDragging = false;
                document.onmouseup = null;
                document.onmousemove = null;
            };

            const dragTouchStart = e => {
                if (!e.target.closest("#ls-openButton")) return;

                const touch = e.touches[0];
                initialX = touch.clientX;
                initialY = touch.clientY;
                startX = touch.clientX - elmnt.offsetLeft;
                startY = touch.clientY - elmnt.offsetTop;
                isDragging = true;
                hasMoved = false;

                elmnt.addEventListener("touchend", closeTouchDrag);
                elmnt.addEventListener("touchmove", elementTouchDrag);
            };

            const elementTouchDrag = e => {
                if (!isDragging) return;
                e.preventDefault();

                const touch = e.touches[0];

                if (
                    Math.abs(touch.clientX - initialX) > 5 ||
                    Math.abs(touch.clientY - initialY) > 5
                ) {
                    hasMoved = true;
                    elmnt.style.pointerEvents = "none";
                }

                const newLeft = touch.clientX - startX;
                const newTop = touch.clientY - startY;

                const maxX = window.innerWidth - 80;
                const maxY = window.innerHeight - 80;
                const minX = 20;
                const minY = 20;

                elmnt.style.left = Math.min(Math.max(minX, newLeft), maxX) + "px";
                elmnt.style.top = Math.min(Math.max(minY, newTop), maxY) + "px";
            };

            const closeTouchDrag = e => {
                if (hasMoved) {
                    e.preventDefault();
                    setTimeout(() => {
                        elmnt.style.pointerEvents = "auto";
                    }, 100);
                }

                isDragging = false;
                elmnt.removeEventListener("touchend", closeTouchDrag);
                elmnt.removeEventListener("touchmove", elementTouchDrag);
            };
            elmnt.onmousedown = dragMouseDown;
            elmnt.addEventListener("touchstart", dragTouchStart);
        };

        const interval = setInterval(() => {
            const button = document.getElementById("ls-openButton");
            if (button) {
                button.style.position = "fixed";
                button.style.cursor = "move";
                button.style.width = "100px";
                button.style.height = "100px";
                button.style.padding = "0";
                button.style.margin = "0";
                button.style.display = "flex";
                button.style.alignItems = "center";
                button.style.justifyContent = "center";
                button.style.overflow = "visible";

                const parentElement = button.parentElement;
                if (parentElement) {
                    parentElement.style.cursor = "auto";
                    parentElement.style.width = "auto";
                    parentElement.style.height = "auto";
                    Array.from(parentElement.children).forEach(child => {
                        if (child.id !== "ls-openButton") {
                            child.style.cursor = "auto";
                        }
                    });
                }

                dragElement(button);
                clearInterval(interval);
            }
        }, 1000);
    };

    useEffect(() => {
        let script1, script2;

        const initSupsis = () => {
            if (!isCompanyUser || !isLoggedIn) return;
            script1 = document.createElement("script");
            script1.innerHTML = `window.supsis = window.supsis || function () { (supsis.q = supsis.q || []).push(arguments) }; supsis.l = +new Date;`;
            document.body.appendChild(script1);

            script2 = document.createElement("script");
            script2.src = "https://vigo.visitor.supsis.live/static/js/loader.js";
            script2.async = true;
            script2.defer = true;

            script2.onload = () => {
                setTimeout(() => {
                    if (window.supsis) {
                        const companyName = stores?.meStore?.companies?.[0]?.companyName;
                        const subCompanyName = stores?.meStore?.subcompanies?.[0]?.name;
                        const city = stores?.meStore?.subcompanies?.[0]?.city;
                        const fullName = stores?.meStore?.fullName || "";
                        const companyId = stores?.meStore?.companies?.[0]?._id;
                        const companyType =
                            stores?.meStore?.pool?.integrationType === "dedicatedIntegration"
                                ? "Havuz"
                                : stores?.meStore?.subcompanies?.[0]?.subCompanyWorkType ===
                                  "dedicated"
                                ? "Dedike"
                                : stores?.meStore?.subcompanies?.[0]?.subCompanyWorkType === "pool"
                                ? "Havuz"
                                : stores?.meStore?.subcompanies?.[0]?.subCompanyWorkType;
                        const userData = {
                            fullname: subCompanyName ? `${subCompanyName} ${fullName}` : fullName,
                            email: `${
                                config?.isDev
                                    ? "https://app-dev.vigo.ist/"
                                    : "https://app.vigo.ist/"
                            }companies/${companyId}`,
                            phone: stores?.meStore?.phone || "",
                            company_type: companyType,
                            company_name: companyName,
                            subcompany_name: subCompanyName,
                            subcompany_city: city
                        };

                        window.supsis("setUserData", userData);
                        window.supsis("ready", supsis => {
                            supsis.setContactProperty({
                                company_type: companyType,
                                company_name: companyName,
                                subcompany_name: subCompanyName,
                                subcompany_city: city
                            });
                        });

                        window.supsis("widget:visibility", true);
                    }
                }, 0);
            };

            document.body.appendChild(script2);
        };

        const cleanup = () => {
            if (window.supsis) {
                window.supsis("widget:visibility", false);
                window.supsis("destroy");
            }

            if (script1?.parentNode) {
                script1.parentNode.removeChild(script1);
            }
            if (script2?.parentNode) {
                script2.parentNode.removeChild(script2);
            }

            const container = document.querySelector(".supsis-widget-container");
            if (container) {
                container.remove();
            }

            delete window.supsis;
        };

        if (isLoggedIn) {
            cleanup();
            initSupsis();
            draggableSupsis();
        }

        return cleanup;
    }, [isLoggedIn]);

    return useObserver(() =>
        !isLoggedIn || window.location.pathname.indexOf("password-reset") !== -1 ? (
            <LoginLayout>{props.children}</LoginLayout>
        ) : isPoolCustomer && isUATCustomer ? (
            <CustomerTheme content={props.children} />
        ) : (
            <LayoutContainer>
                <Loading />
                {/* <WhatsappSupportButton /> */}

                {isMap(history.location.pathname) && isMobile && (
                    <MobileMenuToggle
                        pathname={history.location.pathname}
                        onClick={() => setCollapsed(!collapsed)}
                    >
                        {!collapsed ? "" : <VigoMobileLogo />}
                    </MobileMenuToggle>
                )}

                {isLoggedIn && !stores.appStore.loading && (
                    <LayoutSider
                        collapsedWidth={isMobile ? 60 : 80}
                        collapsible
                        collapsed={collapsed}
                        onCollapse={onCollapse}
                        pathname={history.location.pathname}
                    >
                        <LogoContainer onClick={() => setCollapsed(!collapsed)}>
                            <VigoMobileLogo />
                            <LogoImg src={Logo} />
                        </LogoContainer>

                        <Menu
                            theme="dark"
                            selectedKeys={[
                                stores.appStore.currentPath
                                    ? stores.appStore.currentPath.selectedPath
                                    : getStartRoute({
                                          role: stores?.meStore?.role,
                                          isPoolCustomer: stores?.meStore?.isPoolCustomer,
                                          isUatCustomer: stores?.meStore?.isUatCustomer
                                      })
                            ]}
                            mode="inline"
                            onClick={({key, domEvent}) => {
                                domEvent.preventDefault();
                                history.push(key);
                            }}
                        >
                            {(stores.meStore.checkRole("isCompanyUser")
                                ? stores.meStore.checkRole("isPoolCustomer") &&
                                  stores.meStore.checkRole("isUatCustomer")
                                    ? poolAndUatCustomerRoutes
                                    : stores.meStore.checkRole("isPoolCustomer")
                                    ? poolCustomerRoutes
                                    : stores.meStore.checkRole("isNonApiIntegratedCustomer")
                                    ? nonApiIntegratedCustomerRoutes
                                    : customerRoutes
                                : routes
                            )
                                .filter(
                                    route =>
                                        route.navPosition === "top" && route.roles.includes(role)
                                )
                                .map((route, i, arr) => (
                                    <React.Fragment key={i}>
                                        {!route.subMenus && (
                                            <Menu.Item
                                                style={{
                                                    paddingBottom:
                                                        arr.length - 1 === i && arr.length > 1
                                                            ? "80px"
                                                            : "0px"
                                                }}
                                                data-testid={route["data-testid"]}
                                                key={
                                                    route.companyUserPage &&
                                                    route.path === "/companies"
                                                        ? "/companies/" +
                                                          stores?.meStore?.company?._id
                                                        : route.path
                                                }
                                                disabled={
                                                    route.companyUserPage &&
                                                    !stores?.meStore?.company?._id
                                                }
                                            >
                                                <Link
                                                    to={
                                                        route.companyUserPage &&
                                                        route.path === "/companies"
                                                            ? "/companies/" +
                                                              stores?.meStore?.company?._id
                                                            : route.path
                                                    }
                                                >
                                                    {route.icon}
                                                    <MenuText>{route.name()}</MenuText>
                                                </Link>
                                            </Menu.Item>
                                        )}
                                        {route.subMenus && (
                                            <SubMenu
                                                key={
                                                    route.companyUserPage &&
                                                    route.path === "/companies"
                                                        ? "/companies/" +
                                                          stores?.meStore?.company?._id
                                                        : route.path
                                                }
                                                icon={route.icon}
                                                title={<MenuText>{route.name()}</MenuText>}
                                            >
                                                {route.subMenus
                                                    .filter(subRoute =>
                                                        subRoute.roles.includes(role)
                                                    )
                                                    .map(subRoute => (
                                                        <Menu.Item
                                                            className={
                                                                isMenuActive(
                                                                    `${route.path}${subRoute.subPath}`
                                                                )
                                                                    ? "ant-menu-item-selected"
                                                                    : ""
                                                            }
                                                            data-testid={subRoute["data-testid"]}
                                                            key={`${route.path}${subRoute.subPath}`}
                                                            disabled={
                                                                subRoute.companyUserPage &&
                                                                !stores?.meStore?.company?._id
                                                            }
                                                        >
                                                            <Link
                                                                to={`${route.path}${subRoute.subPath}`}
                                                            >
                                                                {subRoute.icon}
                                                                <MenuText>
                                                                    {subRoute.name()}
                                                                </MenuText>
                                                            </Link>
                                                        </Menu.Item>
                                                    ))}
                                            </SubMenu>
                                        )}
                                    </React.Fragment>
                                ))}
                        </Menu>
                        <BottomMenu
                            theme="dark"
                            selectedKeys={[
                                stores.appStore.currentPath &&
                                    stores.appStore.currentPath.selectedPath
                            ]}
                            mode="inline"
                            onClick={({key, domEvent}) => {
                                domEvent.preventDefault();
                                if (key === "logout") {
                                    stores.meStore.logout();
                                    history.push("/login");
                                } else {
                                    history.push(key);
                                }
                            }}
                        >
                            {(stores.meStore.checkRole("isCompanyUser")
                                ? stores.meStore.checkRole("isPoolCustomer") &&
                                  stores.meStore.checkRole("isUatCustomer")
                                    ? poolAndUatCustomerRoutes
                                    : stores.meStore.checkRole("isPoolCustomer")
                                    ? poolCustomerRoutes
                                    : stores.meStore.checkRole("isNonApiIntegratedCustomer")
                                    ? nonApiIntegratedCustomerRoutes
                                    : customerRoutes
                                : routes
                            )
                                .filter(
                                    route =>
                                        route.navPosition === "bottom" && route.roles.includes(role)
                                )
                                .map(route => (
                                    <Menu.Item key={route.path}>
                                        <Link to={route.path}>
                                            {route.icon}
                                            <MenuText>{route.name()}</MenuText>
                                        </Link>
                                    </Menu.Item>
                                ))}
                        </BottomMenu>
                    </LayoutSider>
                )}
                <RightLayout
                    isloggedin={isLoggedIn ? 1 : undefined}
                    collapsed={collapsed ? 1 : undefined}
                    pathname={history.location.pathname}
                >
                    <div data-testid="mainLayoutHeader">
                        {isLoggedIn && (
                            <LayoutHeader
                                title={
                                    isMobile && isMap(history.location.pathname)
                                        ? ""
                                        : role === "cUser"
                                        ? companies.length > 1
                                            ? "Vigo Panel"
                                            : `${companies[0].companyName} / Yönetim Paneli`
                                        : company
                                        ? `${company.companyName} / Yönetim Paneli`
                                        : "Vigo Panel"
                                }
                                extra={extra}
                            ></LayoutHeader>
                        )}
                    </div>
                    <LayoutContent pathname={pathname} isloggedin={isLoggedIn ? 1 : undefined}>
                        <ChildrenContainer pathname={pathname}>{props.children}</ChildrenContainer>
                    </LayoutContent>
                    <LayoutFooter>Vigo Panel {moment().year() || 2021}</LayoutFooter>
                </RightLayout>
            </LayoutContainer>
        )
    );
});
